import { useState } from 'react';
import { Pencil, Plus, Sparkle, Trash, X } from '@phosphor-icons/react';
import type { Question } from '@cvs/shared';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Button, Modal } from './ui';

/** Per-module quiz editor (add quiz, add/edit/delete questions). Self-contained. */
export function QuizManager({ moduleId, moduleTitle }: { moduleId: string; moduleTitle: string }) {
  const [refresh, setRefresh] = useState(0);
  const meta = useAsync(() => api.moduleLessons(moduleId), [moduleId, refresh]);
  const quizId = meta.data?.quizId ?? null;
  const quizState = useAsync(() => (quizId ? api.quiz(quizId) : Promise.resolve(null)), [quizId, refresh]);
  const [modal, setModal] = useState<{ question?: Question } | null>(null);
  const [genOpen, setGenOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>, after?: () => void) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setRefresh((n) => n + 1);
      after?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  if (meta.loading) return <p className="px-4 py-2 text-sm text-slate-400">Loading quiz…</p>;

  return (
    <div className="border-t border-slate-100 px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="label-mono">Knowledge check</span>
        {!quizId ? (
          <Button
            variant="secondary"
            icon={Plus}
            loading={busy}
            onClick={() => void run(() => api.admin.createQuiz(moduleId, `${moduleTitle} knowledge check`))}
          >
            Add quiz
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="secondary" icon={Sparkle} onClick={() => setGenOpen(true)}>
              Generate with AI
            </Button>
            <Button variant="secondary" icon={Plus} onClick={() => setModal({})}>
              Add question
            </Button>
            <button
              className="btn-ghost p-2 text-rose-600"
              aria-label="Delete quiz"
              onClick={() => {
                if (confirm('Delete the quiz and all its questions?')) void run(() => api.admin.deleteQuiz(quizId));
              }}
            >
              <Trash size={18} />
            </button>
          </div>
        )}
      </div>
      {error && <p className="mb-2 text-sm text-rose-600">{error}</p>}

      {quizId && quizState.data && (
        <ol className="space-y-2">
          {quizState.data.questions.map((q, i) => (
            <li key={q.id} className="rounded-lg bg-white p-3 text-sm shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium text-slate-700">
                  {i + 1}. {q.prompt}
                </span>
                <span className="flex shrink-0 gap-1">
                  <button className="btn-ghost p-1.5" aria-label="Edit question" onClick={() => setModal({ question: q })}>
                    <Pencil size={16} />
                  </button>
                  <button
                    className="btn-ghost p-1.5 text-rose-600"
                    aria-label="Delete question"
                    onClick={() => void run(() => api.admin.deleteQuestion(q.id))}
                  >
                    <Trash size={16} />
                  </button>
                </span>
              </div>
              <ul className="mt-1 space-y-0.5 text-xs text-slate-500">
                {q.options.map((o, oi) => (
                  <li key={oi} className={oi === q.correctIndex ? 'font-semibold text-emerald-600' : ''}>
                    {oi === q.correctIndex ? '✓ ' : '• '}
                    {o}
                  </li>
                ))}
              </ul>
            </li>
          ))}
          {quizState.data.questions.length === 0 && <li className="text-sm text-slate-400">No questions yet.</li>}
        </ol>
      )}

      {modal && quizId && (
        <Modal open onClose={() => setModal(null)} title={modal.question ? 'Edit question' : 'Add question'}>
          <QuestionForm
            key={modal.question?.id ?? 'new'}
            initial={modal.question}
            busy={busy}
            onCancel={() => setModal(null)}
            onSubmit={(v) =>
              run(
                () =>
                  modal.question
                    ? api.admin.updateQuestion(modal.question.id, v)
                    : api.admin.createQuestion(quizId, v),
                () => setModal(null),
              )
            }
          />
        </Modal>
      )}

      {genOpen && quizId && (
        <Modal open onClose={() => setGenOpen(false)} title="Generate questions with AI">
          <GenerateForm
            defaultTopic={moduleTitle}
            busy={busy}
            onCancel={() => setGenOpen(false)}
            onSubmit={(v) => run(() => api.admin.generateQuestions(quizId, v), () => setGenOpen(false))}
          />
        </Modal>
      )}
    </div>
  );
}

function GenerateForm({
  defaultTopic,
  busy,
  onSubmit,
  onCancel,
}: {
  defaultTopic: string;
  busy: boolean;
  onSubmit: (v: { topic: string; count: number; useTranscript: boolean }) => void;
  onCancel: () => void;
}) {
  const [topic, setTopic] = useState(defaultTopic);
  const [count, setCount] = useState(5);
  const [useTranscript, setUseTranscript] = useState(false);

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ topic: topic.trim(), count, useTranscript });
      }}
    >
      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-3">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-brand-teal"
          checked={useTranscript}
          onChange={(e) => setUseTranscript(e.target.checked)}
        />
        <span className="text-sm">
          <span className="font-semibold text-brand-navy">Base questions on the class transcript</span>
          <span className="block text-xs text-slate-500">
            Questions come from what was actually taught. Needs a transcript on this class.
          </span>
        </span>
      </label>
      <label className="block">
        <span className="label-mono mb-1 block">{useTranscript ? 'Focus area (optional)' : 'Topic'}</span>
        <input
          className="input"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder={useTranscript ? 'e.g. just the IP addressing part' : 'e.g. Azure RBAC and least privilege'}
          required={!useTranscript}
        />
      </label>
      <label className="block">
        <span className="label-mono mb-1 block">How many questions</span>
        <select className="input" value={count} onChange={(e) => setCount(Number(e.target.value))}>
          {[3, 5, 8, 10].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <p className="text-xs text-slate-400">
        AI drafts multiple-choice questions and adds them to this quiz. Review and edit them afterwards — delete any you
        don't like. Generating can take a few seconds.
      </p>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" icon={Sparkle} loading={busy} disabled={!useTranscript && !topic.trim()}>
          {busy ? 'Generating…' : 'Generate'}
        </Button>
      </div>
    </form>
  );
}

function QuestionForm({
  initial,
  busy,
  onSubmit,
  onCancel,
}: {
  initial?: Question;
  busy: boolean;
  onSubmit: (v: { prompt: string; options: string[]; correctIndex: number; explanation: string }) => void;
  onCancel: () => void;
}) {
  const [prompt, setPrompt] = useState(initial?.prompt ?? '');
  const [options, setOptions] = useState<string[]>(initial?.options ?? ['', '', '', '']);
  const [correctIndex, setCorrectIndex] = useState(initial?.correctIndex ?? 0);
  const [explanation, setExplanation] = useState(initial?.explanation ?? '');

  const setOpt = (i: number, v: string) => setOptions((o) => o.map((x, xi) => (xi === i ? v : x)));
  const addOpt = () => setOptions((o) => [...o, '']);
  const removeOpt = (i: number) =>
    setOptions((o) => {
      const next = o.filter((_, xi) => xi !== i);
      if (correctIndex >= next.length) setCorrectIndex(Math.max(0, next.length - 1));
      return next;
    });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        const correctValue = options[correctIndex];
        const cleaned = options.map((o) => o.trim()).filter(Boolean);
        const idx = Math.max(0, cleaned.indexOf((correctValue ?? '').trim()));
        onSubmit({ prompt: prompt.trim(), options: cleaned, correctIndex: idx, explanation: explanation.trim() });
      }}
    >
      <label className="block">
        <span className="label-mono mb-1 block">Question</span>
        <input className="input" value={prompt} onChange={(e) => setPrompt(e.target.value)} required />
      </label>
      <div>
        <span className="label-mono mb-1 block">Options (select the correct one)</span>
        <div className="space-y-2">
          {options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="radio"
                name="correct"
                checked={correctIndex === i}
                onChange={() => setCorrectIndex(i)}
                aria-label={`Mark option ${i + 1} correct`}
              />
              <input className="input" value={o} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Option ${i + 1}`} />
              {options.length > 2 && (
                <button type="button" className="btn-ghost p-1.5 text-rose-600" aria-label="Remove option" onClick={() => removeOpt(i)}>
                  <X size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
        <Button type="button" variant="ghost" icon={Plus} className="mt-2" onClick={addOpt}>
          Add option
        </Button>
      </div>
      <label className="block">
        <span className="label-mono mb-1 block">Explanation</span>
        <textarea className="input" rows={2} value={explanation} onChange={(e) => setExplanation(e.target.value)} />
      </label>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={busy} disabled={!prompt.trim() || options.filter((o) => o.trim()).length < 2}>
          Save
        </Button>
      </div>
    </form>
  );
}
